import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { adminTenantsAPI, adminUsersAPI, adminAuthAPI, adminPhase2AuditAPI, adminPlayerHelpAssistantAPI, adminPlayerHelpWizardAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { setAdminTenantInfo } from '../utils/tenantHelper';
import { slugFromName, validateSlug, normalizeSlugInput } from '../utils/slugUtils';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  position: relative;
  overflow-x: visible;
  @media (max-width: 768px) {
    flex-direction: column;
    overflow-x: hidden;
  }
`;

const Sidebar = styled.aside`
  width: 250px;
  background: #1f2937;
  color: white;
  padding: 0;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1000;
  transition: transform 0.25s ease-out;
  @media (max-width: 768px) {
    transform: ${(p) => (p.$isOpen ? 'translateX(0)' : 'translateX(-100%)')};
    width: min(280px, 85vw);
    box-shadow: ${(p) => (p.$isOpen ? '2px 0 10px rgba(0,0,0,0.1)' : 'none')};
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${(p) => (p.$isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 1001;
  background: #1f2937;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  @media (max-width: 768px) { display: block; }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid #374151;
  .logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 1rem;
    img { width: 40px; height: 40px; border-radius: 8px; flex-shrink: 0; }
    h1 { font-size: 1.2rem; font-weight: 700; margin: 0; }
    p { font-size: 0.75rem; opacity: 0.8; margin: 0; margin-top: 0.25rem; }
  }
  .admin-info p { margin: 0; font-size: 0.875rem; color: #d1d5db; word-break: break-word; }
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    .logo-section h1 { font-size: 1.1rem; }
  }
`;

const Navigation = styled.nav` padding: 1rem 0; `;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  border-left: 3px solid transparent;
  &:hover { background: rgba(255, 255, 255, 0.1); }
  &.active { background: rgba(30, 64, 175, 0.2); border-left-color: #043663; }
  .nav-icon { font-size: 1.25rem; width: 20px; text-align: center; flex-shrink: 0; }
  .nav-text { font-weight: 500; flex: 1; }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 250px;
  padding: 2rem;
  min-height: 100vh;
  @media (max-width: 768px) {
    margin-left: 0;
    padding: 1rem;
    padding-top: 4rem;
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const Title = styled.h1`
  color: #1f2937;
  font-size: 1.5rem;
  margin: 0 0 0.5rem 0;
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0 0 1.5rem 0;
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1.5rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  min-width: 0;
  &:focus { outline: none; border-color: #043663; }
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  background: white;
  min-width: 0;
  &:focus { outline: none; border-color: #043663; }
  @media (max-width: 768px) {
    font-size: 0.9rem;
    width: 100%;
  }
`;

const Button = styled.button`
  background: #043663;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  min-width: 0;
  &:hover { background: #032a4a; }
  &:disabled { background: #9ca3af; cursor: not-allowed; }
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.6rem 1rem;
  }
`;

const ButtonSecondary = styled(Button)`
  background: #6b7280;
  &:hover { background: #4b5563; }
`;

const ButtonDanger = styled(Button)`
  background: #b91c1c;
  &:hover { background: #991b1b; }
`;

const ActionsCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
`;

const TableScrollWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -0.5rem;
  @media (min-width: 769px) {
    margin: 0;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: 600px;
  @media (max-width: 768px) {
    font-size: 0.85rem;
    min-width: 560px;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
  color: #374151;
  font-weight: 600;
  white-space: nowrap;
  @media (max-width: 768px) {
    padding: 0.5rem 0.6rem;
  }
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
  @media (max-width: 768px) {
    padding: 0.5rem 0.6rem;
  }
`;

const Tr = styled.tr`
  &:hover { background: #f9fafb; }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(p) => (p.$active ? '#d1fae5' : '#fee2e2')};
  color: ${(p) => (p.$active ? '#065f46' : '#991b1b')};
`;


const OptionCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 1.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover { border-color: #043663; box-shadow: 0 2px 8px rgba(4, 54, 99, 0.15); }
  .option-title { font-size: 1.1rem; font-weight: 600; color: #1f2937; margin: 0 0 0.25rem 0; }
  .option-desc { font-size: 0.875rem; color: #6b7280; margin: 0; }
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.25rem;
    .option-title { font-size: 1rem; }
    .option-desc { font-size: 0.8125rem; }
  }
`;

const OptionRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const EditUserFormWrap = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  select {
    min-width: 0;
  }
`;

const ProfileForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 420px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #043663;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 1rem;
  &:hover { text-decoration: underline; }
`;

const SlugError = styled.span`
  font-size: 0.8rem;
  color: #dc2626;
  margin-top: 0.15rem;
`;

const SuperAdminTenantsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { adminUser, logout, updateUser, isLoading: authLoading } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('add-tenant');
  const [addTenantSubType, setAddTenantSubType] = useState(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState('country');
  const [countryCode, setCountryCode] = useState('');
  const [countryTenantId, setCountryTenantId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userTenantId, setUserTenantId] = useState('');
  const [userRole, setUserRole] = useState('admin');
  const [editingUser, setEditingUser] = useState(null);
  const [editTenantId, setEditTenantId] = useState('');
  const [editRole, setEditRole] = useState('admin');
  const [profileFirstName, setProfileFirstName] = useState('');
  const [profileLastName, setProfileLastName] = useState('');
  const [profileEmail, setProfileEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [auditExportLoading, setAuditExportLoading] = useState(false);

  const { data: phaSettingRes, isLoading: phaSettingLoading } = useQuery(
    'admin-player-help-assistant-setting',
    () => adminPlayerHelpAssistantAPI.getSetting(),
    { enabled: !!adminUser && activeSection === 'player-help-assistant' }
  );
  const phaEnabled = phaSettingRes?.data?.enabled === true;

  const phaMutation = useMutation(
    (enabled) => adminPlayerHelpAssistantAPI.putSetting({ enabled }),
    {
      onSuccess: (res) => {
        if (res?.success) {
          toast.success(res?.message || 'Setting saved.');
          queryClient.invalidateQueries('admin-player-help-assistant-setting');
        }
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to save');
      },
    }
  );

  const { data: phwSettingRes, isLoading: phwSettingLoading } = useQuery(
    'admin-player-help-wizard-setting',
    () => adminPlayerHelpWizardAPI.getSetting(),
    { enabled: !!adminUser && activeSection === 'player-help-wizard' }
  );
  const phwEnabled = phwSettingRes?.data?.enabled === true;

  const phwMutation = useMutation(
    (enabled) => adminPlayerHelpWizardAPI.putSetting({ enabled }),
    {
      onSuccess: (res) => {
        if (res?.success) {
          toast.success(res?.message || 'Setting saved.');
          queryClient.invalidateQueries('admin-player-help-wizard-setting');
        }
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to save');
      },
    }
  );

  const { data: tenantsResponse, isLoading: listLoading } = useQuery(
    'admin-tenants-list',
    () => adminTenantsAPI.getList(),
    { enabled: !!adminUser }
  );
  const tenants = tenantsResponse?.data ?? [];

  const { data: adminUsersResponse, isLoading: usersLoading } = useQuery(
    'admin-users-list',
    () => adminUsersAPI.getList(),
    { enabled: !!adminUser && activeSection === 'users-roles' }
  );
  const adminUsers = adminUsersResponse?.data ?? [];

  const { data: profileResponse } = useQuery(
    'admin-profile',
    () => adminAuthAPI.getProfile(),
    { enabled: !!adminUser && activeSection === 'profile' }
  );
  const { data: phase2AuditResponse, isLoading: phase2AuditLoading } = useQuery(
    'admin-phase2-membership-audit',
    () => adminPhase2AuditAPI.getMembershipAudit(),
    { enabled: !!adminUser && activeSection === 'phase2-audit' }
  );
  const phase2Audit = phase2AuditResponse?.data || null;
  useEffect(() => {
    if (profileResponse?.success && profileResponse?.user) {
      const u = profileResponse.user;
      setProfileFirstName(u.first_name ?? '');
      setProfileLastName(u.last_name ?? '');
      setProfileEmail(u.email ?? '');
    }
  }, [profileResponse]);

  const updateProfileMutation = useMutation(
    (data) => adminAuthAPI.updateProfile(data),
    {
      onSuccess: (res) => {
        if (res?.success && res?.user) {
          toast.success('Profile updated');
          updateUser(res.user);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }
      },
      onError: (err) => {
        const msg = err?.response?.data?.message || err?.message || 'Failed to update profile';
        toast.error(msg);
      },
    }
  );

  const updateTenantMutation = useMutation(
    ({ id, data }) => adminTenantsAPI.update(id, data),
    {
      onSuccess: (res, vars) => {
        const active = vars?.data?.is_active;
        if (active === false) {
          toast.success('Tenant disabled — hidden from player Tournaments');
        } else if (active === true) {
          toast.success('Tenant enabled');
        } else {
          toast.success('Tenant updated');
        }
        queryClient.invalidateQueries('admin-tenants-list');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to update tenant');
      },
    }
  );

  const handleToggleTenantActive = (t) => {
    if (t.type === 'country') return;
    const nextActive = !t.is_active;
    const label = t.name || t.slug || 'this tenant';
    if (!nextActive) {
      const ok = window.confirm(
        `Disable "${label}"?\n\nPlayers will no longer see this league in Tournaments. Country tenants are not affected.`
      );
      if (!ok) return;
    }
    updateTenantMutation.mutate({ id: t.id, data: { is_active: nextActive } });
  };

  const createMutation = useMutation(
    (payload) => adminTenantsAPI.create(payload),
    {
      onSuccess: (res) => {
        if (res?.success && res?.data) {
          toast.success(`Tenant "${res.data.name}" created`);
          setName('');
          setSlug('');
          setType('country');
          setCountryCode('');
          setCountryTenantId('');
          setAddTenantSubType(null);
          queryClient.invalidateQueries('admin-tenants-list');
        }
      },
      onError: (err) => {
        const msg = err?.response?.data?.message || err?.message || 'Failed to create tenant';
        toast.error(msg);
      },
    }
  );

  const createUserMutation = useMutation(
    (payload) => adminUsersAPI.create(payload),
    {
      onSuccess: (res) => {
        if (res?.success && res?.data) {
          toast.success(`Admin user "${res.data.email}" created`);
          setUserEmail('');
          setUserPassword('');
          setUserFirstName('');
          setUserLastName('');
          setUserTenantId('');
          setUserRole('admin');
          queryClient.invalidateQueries('admin-users-list');
        }
      },
      onError: (err) => {
        const msg = err?.response?.data?.message || err?.message || 'Failed to create admin user';
        toast.error(msg);
      },
    }
  );

  const updateUserMutation = useMutation(
    ({ id, data }) => adminUsersAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('Admin user updated');
        setEditingUser(null);
        queryClient.invalidateQueries('admin-users-list');
      },
      onError: (err) => {
        const msg = err?.response?.data?.message || err?.message || 'Failed to update admin user';
        toast.error(msg);
      },
    }
  );

  const removeUserMutation = useMutation((id) => adminUsersAPI.remove(id), {
    onSuccess: (res) => {
      toast.success(res?.message || 'Admin access removed');
      setEditingUser(null);
      queryClient.invalidateQueries('admin-users-list');
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || err?.message || 'Failed to remove admin user';
      toast.error(msg);
    },
  });

  const handleRemoveAdminUser = (u) => {
    if (String(u.id) === String(adminUser?.id)) {
      toast.error('You cannot remove your own admin account.');
      return;
    }
    if (u.role === 'super_admin') {
      toast.error('Super Admin accounts cannot be removed here.');
      return;
    }
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
    const ok = window.confirm(
      `Remove admin access for ${name} (${u.email})?\n\nThey will no longer be able to log into the admin platform.`
    );
    if (!ok) return;
    removeUserMutation.mutate(u.id);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!adminUser) {
      navigate('/admin/login', { replace: true });
      return;
    }
    if (!adminUser.isSuperAdmin) {
      toast.error('Access denied. Super Admin only.');
      navigate('/admin/login', { replace: true });
    }
  }, [adminUser, authLoading, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const slugVal = (slug && slug.trim()) ? slug.trim() : slugFromName(name.trim());
    const slugValidation = validateSlug(slugVal);
    if (!slugValidation.valid) {
      toast.error(slugValidation.error || 'Invalid slug');
      return;
    }
    if (type === 'court' && !countryTenantId) {
      toast.error('Country is required for Court / Club');
      return;
    }
    createMutation.mutate({
      name: name.trim(),
      slug: slugVal,
      type,
      country_code: countryCode.trim() || undefined,
      country_tenant_id: type === 'court' ? countryTenantId || undefined : undefined,
    });
  };

  const handleOpenTenant = (t) => {
    const tenantPayload = { id: t.id, slug: t.slug, name: t.name, type: t.type };
    setAdminTenantInfo(tenantPayload);
    navigate(`/${t.slug}/admin/dashboard`, { state: { tenantFromSuperAdmin: tenantPayload } });
  };

  const handleAddTenantType = (subType) => {
    setAddTenantSubType(subType);
    setType(subType);
    setCountryCode(subType === 'country' ? '' : '');
    setCountryTenantId(subType === 'country' ? '' : '');
  };

  const handleCreateUser = (e) => {
    e.preventDefault();
    if (!userEmail.trim() || !userPassword || !userFirstName.trim() || !userLastName.trim()) {
      toast.error('Email, password, first name, and last name are required');
      return;
    }
    if (userRole !== 'super_admin' && !userTenantId) {
      toast.error('Select a tenant for this admin, or choose Super Admin (all tenants)');
      return;
    }
    createUserMutation.mutate({
      email: userEmail.trim(),
      password: userPassword,
      first_name: userFirstName.trim(),
      last_name: userLastName.trim(),
      tenant_id: userTenantId || null,
      role: userRole,
    });
  };

  const handleStartEditUser = (u) => {
    setEditingUser(u);
    setEditTenantId(u.tenant_id || '');
    setEditRole(u.role || 'admin');
  };

  const handleSaveEditUser = (e) => {
    e.preventDefault();
    if (!editingUser) return;
    updateUserMutation.mutate({
      id: editingUser.id,
      data: {
        tenant_id: editTenantId || null,
        role: editRole,
        role_id: null,
      },
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleSaveProfile = (e) => {
    e.preventDefault();
    if (!profileFirstName.trim() || !profileLastName.trim() || !profileEmail.trim()) {
      toast.error('First name, last name and email are required');
      return;
    }
    if (newPassword || confirmPassword || currentPassword) {
      if (!currentPassword) {
        toast.error('Enter current password to change password');
        return;
      }
      if (!newPassword || newPassword.length < 6) {
        toast.error('New password must be at least 6 characters');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('New password and confirmation do not match');
        return;
      }
    }
    const payload = {
      first_name: profileFirstName.trim(),
      last_name: profileLastName.trim(),
      email: profileEmail.trim().toLowerCase(),
    };
    if (newPassword) {
      payload.current_password = currentPassword;
      payload.new_password = newPassword;
    }
    updateProfileMutation.mutate(payload);
  };

  const handleExportPhase2Audit = async () => {
    try {
      setAuditExportLoading(true);
      const blob = await adminPhase2AuditAPI.exportMembershipAudit();
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `phase2-membership-audit-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit report downloaded');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to export audit report';
      toast.error(msg);
    } finally {
      setAuditExportLoading(false);
    }
  };

  // Stripe Connect is done in each tenant's admin: Settings > General (not here)

  if (authLoading || !adminUser) {
    return (
      <PageContainer>
        <MainContent style={{ marginLeft: 0 }}>
          <p>Loading...</p>
        </MainContent>
      </PageContainer>
    );
  }

  if (!adminUser.isSuperAdmin) {
    return null;
  }

  return (
    <PageContainer>
      <HamburgerButton type="button" aria-label="Toggle menu" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '✕' : '☰'}
      </HamburgerButton>
      <SidebarOverlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <Sidebar $isOpen={sidebarOpen}>
        <SidebarHeader>
          <div className="logo-section">
            <img src="/images/logo/logo.png" alt="Logo" />
            <div>
              <h1>Super Admin</h1>
              <p>Tenant & system management</p>
            </div>
          </div>
          <div className="admin-info">
            <p>Welcome, {adminUser.name}</p>
            <p>{adminUser.email}</p>
          </div>
        </SidebarHeader>
        <Navigation>
          <NavItem
            className={activeSection === 'add-tenant' ? 'active' : ''}
            onClick={() => { setActiveSection('add-tenant'); setAddTenantSubType(null); if (window.innerWidth <= 768) setSidebarOpen(false); }}
          >
            <span className="nav-icon">➕</span>
            <span className="nav-text">Add a tenant</span>
          </NavItem>
          <NavItem
            className={activeSection === 'all-tenants' ? 'active' : ''}
            onClick={() => { setActiveSection('all-tenants'); if (window.innerWidth <= 768) setSidebarOpen(false); }}
          >
            <span className="nav-icon">🏢</span>
            <span className="nav-text">All tenants</span>
          </NavItem>
          <NavItem
            className={activeSection === 'users-roles' ? 'active' : ''}
            onClick={() => { setActiveSection('users-roles'); setEditingUser(null); if (window.innerWidth <= 768) setSidebarOpen(false); }}
          >
            <span className="nav-icon">👥</span>
            <span className="nav-text">Users & roles</span>
          </NavItem>
          <NavItem
            className={activeSection === 'profile' ? 'active' : ''}
            onClick={() => { setActiveSection('profile'); if (window.innerWidth <= 768) setSidebarOpen(false); }}
          >
            <span className="nav-icon">👤</span>
            <span className="nav-text">My profile</span>
          </NavItem>
          <NavItem
            className={activeSection === 'player-help-assistant' ? 'active' : ''}
            onClick={() => { setActiveSection('player-help-assistant'); if (window.innerWidth <= 768) setSidebarOpen(false); }}
          >
            <span className="nav-icon">💬</span>
            <span className="nav-text">Player help assistant</span>
          </NavItem>
          <NavItem
            className={activeSection === 'player-help-wizard' ? 'active' : ''}
            onClick={() => { setActiveSection('player-help-wizard'); if (window.innerWidth <= 768) setSidebarOpen(false); }}
          >
            <span className="nav-icon">🆘</span>
            <span className="nav-text">Player help wizard</span>
          </NavItem>
          <NavItem
            className={activeSection === 'phase2-audit' ? 'active' : ''}
            onClick={() => { setActiveSection('phase2-audit'); if (window.innerWidth <= 768) setSidebarOpen(false); }}
          >
            <span className="nav-icon">📊</span>
            <span className="nav-text">Phase 2 Audit</span>
          </NavItem>
          <NavItem
            onClick={() => { handleLogout(); if (window.innerWidth <= 768) setSidebarOpen(false); }}
            style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '0.5rem' }}
          >
            <span className="nav-icon">⎋</span>
            <span className="nav-text">Log out</span>
          </NavItem>
        </Navigation>
      </Sidebar>
      <MainContent>
        {activeSection === 'add-tenant' && (
          <ContentCard>
            <Title>Add a tenant</Title>
            <Subtitle>Choose the type of tenant to create, then fill in the form.</Subtitle>
            {addTenantSubType == null ? (
              <OptionRow>
                <OptionCard type="button" onClick={() => handleAddTenantType('country')}>
                  <span className="option-title">Country tenant</span>
                  <span className="option-desc">National or country-level federation (e.g. France, Switzerland).</span>
                </OptionCard>
                <OptionCard type="button" onClick={() => handleAddTenantType('court')}>
                  <span className="option-title">Court / Club</span>
                  <span className="option-desc">A single court, club, or venue.</span>
                </OptionCard>
              </OptionRow>
            ) : (
              <>
                <BackLink type="button" onClick={() => { setAddTenantSubType(null); setName(''); setSlug(''); setCountryCode(''); setCountryTenantId(''); }}>
                  ← Back to tenant type
                </BackLink>
                <Title as="h2" style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                  {addTenantSubType === 'country' ? 'Create country tenant' : 'Create court / club'}
                </Title>
                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>Name *</Label>
                    <Input
                      value={name}
                      onChange={(e) => {
                        const v = e.target.value;
                        setName(v);
                        if (!slug && v) setSlug(slugFromName(v));
                      }}
                      placeholder={addTenantSubType === 'country' ? 'e.g. France Padel League' : 'e.g. Club Name'}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Slug * (URL-friendly)</Label>
                    <Input
                      value={slug}
                      onChange={(e) => setSlug(normalizeSlugInput(e.target.value))}
                      placeholder="e.g. france or club-name"
                    />
                    {slug && (() => { const v = validateSlug(slug); return v.valid ? null : <SlugError>{v.error}</SlugError>; })()}
                  </FormGroup>
                  {addTenantSubType === 'court' && (
                    <FormGroup>
                      <Label>Country *</Label>
                      <Select value={countryTenantId} onChange={(e) => setCountryTenantId(e.target.value)} required>
                        <option value="">Select country tenant</option>
                        {tenants.filter((t) => t.type === 'country' && t.is_active).map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </Select>
                    </FormGroup>
                  )}
                  {addTenantSubType === 'country' && (
                    <FormGroup>
                      <Label>Country code (optional)</Label>
                      <Input
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value.toUpperCase().slice(0, 4))}
                        placeholder="e.g. CH, FR"
                        maxLength={4}
                      />
                    </FormGroup>
                  )}
                  <Button type="submit" disabled={createMutation.isLoading}>
                    {createMutation.isLoading ? 'Creating…' : 'Create tenant'}
                  </Button>
                </Form>
              </>
            )}
          </ContentCard>
        )}

        {activeSection === 'all-tenants' && (
          <ContentCard>
            <Title>All tenants</Title>
            <Subtitle>
              Open any tenant&apos;s admin panel. Disable court/club tenants to hide them from player Tournaments.
              Country tenants always stay active and remain visible to players.
            </Subtitle>
            {listLoading ? (
              <p>Loading tenants…</p>
            ) : tenants.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No tenants yet. Add one from the sidebar.</p>
            ) : (
              <TableScrollWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th>Slug</Th>
                      <Th>Type</Th>
                      <Th>Country</Th>
                      <Th>Status</Th>
                      <Th>Stripe</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((t) => (
                      <Tr key={t.id}>
                        <Td>{t.name}</Td>
                        <Td>{t.slug}</Td>
                        <Td>{t.type}</Td>
                        <Td>{t.country_tenant_name || t.country_code || '—'}</Td>
                        <Td>
                          <Badge $active={t.is_active}>{t.is_active ? 'Active' : 'Inactive'}</Badge>
                        </Td>
                        <Td>
                          {['court', 'organization'].includes(t.type) ? (
                            t.stripe_connected_account_id ? (
                              t.stripe_connection_disconnected_at ? (
                                <Badge $active={false}>Disconnected</Badge>
                              ) : (
                                <Badge $active>Connected</Badge>
                              )
                            ) : (
                              '—'
                            )
                          ) : (
                            '—'
                          )}
                        </Td>
                        <Td>
                          <ActionsCell>
                            <Button type="button" onClick={() => handleOpenTenant(t)}>
                              Open admin
                            </Button>
                            {t.type === 'country' ? (
                              <span style={{ fontSize: '0.8rem', color: '#6b7280' }} title="Country tenants stay enabled">
                                —
                              </span>
                            ) : t.is_active ? (
                              <ButtonDanger
                                type="button"
                                disabled={updateTenantMutation.isLoading}
                                onClick={() => handleToggleTenantActive(t)}
                              >
                                Disable
                              </ButtonDanger>
                            ) : (
                              <ButtonSecondary
                                type="button"
                                disabled={updateTenantMutation.isLoading}
                                onClick={() => handleToggleTenantActive(t)}
                              >
                                Enable
                              </ButtonSecondary>
                            )}
                          </ActionsCell>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableScrollWrapper>
            )}
          </ContentCard>
        )}

        {activeSection === 'users-roles' && (
          <ContentCard>
            <Title>Users & roles</Title>
            <Subtitle>
              Create admin users for a tenant, reassign tenant or role, or remove tenant admins you granted.
              You cannot remove your own account or other Super Admins.
            </Subtitle>
            <Title as="h2" style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Create admin user</Title>
            <Form onSubmit={handleCreateUser} style={{ marginBottom: '2rem' }}>
              <FormGroup>
                <Label>Email *</Label>
                <Input type="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} placeholder="admin@example.com" />
              </FormGroup>
              <FormGroup>
                <Label>Password *</Label>
                <Input type="password" value={userPassword} onChange={(e) => setUserPassword(e.target.value)} placeholder="••••••••" />
              </FormGroup>
              <FormGroup>
                <Label>First name *</Label>
                <Input value={userFirstName} onChange={(e) => setUserFirstName(e.target.value)} placeholder="John" />
              </FormGroup>
              <FormGroup>
                <Label>Last name *</Label>
                <Input value={userLastName} onChange={(e) => setUserLastName(e.target.value)} placeholder="Doe" />
              </FormGroup>
              <FormGroup>
                <Label>Role & scope</Label>
                <Select value={userRole} onChange={(e) => { setUserRole(e.target.value); if (e.target.value === 'super_admin') setUserTenantId(''); }}>
                  <option value="super_admin">Super Admin (all tenants)</option>
                  <option value="admin">Tenant admin (assign tenant below)</option>
                </Select>
              </FormGroup>
              {userRole === 'admin' && (
                <FormGroup>
                  <Label>Tenant *</Label>
                  <Select value={userTenantId} onChange={(e) => setUserTenantId(e.target.value)} required>
                    <option value="">Select tenant</option>
                    {tenants.filter((t) => t.is_active).map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Select>
                </FormGroup>
              )}
              <Button type="submit" disabled={createUserMutation.isLoading}>
                {createUserMutation.isLoading ? 'Creating…' : 'Create admin user'}
              </Button>
            </Form>
            <Title as="h2" style={{ fontSize: '1.2rem', marginBottom: '0.75rem' }}>All admin users</Title>
            {usersLoading ? (
              <p>Loading users…</p>
            ) : adminUsers.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No admin users yet.</p>
            ) : (
              <TableScrollWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Tenant</Th>
                      <Th>Role</Th>
                      <Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map((u) => (
                      <Tr key={u.id}>
                        <Td>{u.first_name} {u.last_name}</Td>
                        <Td>{u.email}</Td>
                        <Td>{(u.role === 'super_admin' || !u.tenant_id) ? 'All tenants' : (u.tenant_name || '—')}</Td>
                        <Td>
                          {u.role === 'super_admin'
                            ? 'Super Admin'
                            : u.role_name
                              ? `Admin (${u.role_name})`
                              : (u.role || 'Admin')}
                        </Td>
                        <Td>
                          {editingUser?.id === u.id ? (
                            <form onSubmit={handleSaveEditUser}>
                              <EditUserFormWrap>
                                <Select value={editRole} onChange={(e) => { setEditRole(e.target.value); if (e.target.value === 'super_admin') setEditTenantId(''); }}>
                                  <option value="super_admin">Super Admin (all tenants)</option>
                                  <option value="admin">Tenant admin</option>
                                </Select>
                                <Select value={editTenantId} onChange={(e) => setEditTenantId(e.target.value)}>
                                  <option value="">— No tenant (Super Admin) —</option>
                                  {tenants.filter((t) => t.is_active).map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                  ))}
                                </Select>
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                  <Button type="submit" disabled={updateUserMutation.isLoading}>Save</Button>
                                  <ButtonSecondary type="button" onClick={() => setEditingUser(null)}>Cancel</ButtonSecondary>
                                </div>
                              </EditUserFormWrap>
                              <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>Saving gives full tenant admin (clears custom role).</span>
                            </form>
                          ) : (
                            <ActionsCell>
                              <Button type="button" onClick={() => handleStartEditUser(u)}>Reassign</Button>
                              {String(u.id) === String(adminUser?.id) ? (
                                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>You</span>
                              ) : u.role === 'super_admin' ? null : (
                                <ButtonDanger
                                  type="button"
                                  disabled={removeUserMutation.isLoading}
                                  onClick={() => handleRemoveAdminUser(u)}
                                >
                                  Remove
                                </ButtonDanger>
                              )}
                            </ActionsCell>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableScrollWrapper>
            )}
          </ContentCard>
        )}

        {activeSection === 'profile' && (
          <ContentCard>
            <Title>My profile</Title>
            <Subtitle>Update your name, email and password. Changes apply to this Super Admin account only.</Subtitle>
            <ProfileForm onSubmit={handleSaveProfile}>
              <FormGroup>
                <Label>First name *</Label>
                <Input
                  value={profileFirstName}
                  onChange={(e) => setProfileFirstName(e.target.value)}
                  placeholder="First name"
                />
              </FormGroup>
              <FormGroup>
                <Label>Last name *</Label>
                <Input
                  value={profileLastName}
                  onChange={(e) => setProfileLastName(e.target.value)}
                  placeholder="Last name"
                />
              </FormGroup>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </FormGroup>
              <Title as="h2" style={{ fontSize: '1.1rem', marginTop: '0.5rem', marginBottom: '0.25rem' }}>Change password</Title>
              <Subtitle style={{ marginBottom: '0.5rem' }}>Leave blank to keep your current password.</Subtitle>
              <FormGroup>
                <Label>Current password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </FormGroup>
              <FormGroup>
                <Label>New password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </FormGroup>
              <FormGroup>
                <Label>Confirm new password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </FormGroup>
              <Button type="submit" disabled={updateProfileMutation.isLoading}>
                {updateProfileMutation.isLoading ? 'Saving…' : 'Save profile'}
              </Button>
            </ProfileForm>
          </ContentCard>
        )}

        {activeSection === 'player-help-assistant' && (
          <ContentCard>
            <Title>Player help assistant</Title>
            <Subtitle>
              Toggles the in-app HPS help assistant on the player dashboard (alpha). Leave off until Phase 2 is ready so
              players are not exposed to incomplete behaviour. The server must also allow the feature: environment variable{' '}
              <code style={{ fontSize: '0.85em', background: '#f3f4f6', padding: '0.1rem 0.35rem', borderRadius: 4 }}>
                HELP_ASSISTANT_ENABLED
              </code>{' '}
              must not be set to &quot;false&quot;, or the assistant API will not load at all.
            </Subtitle>
            {phaSettingLoading ? (
              <p style={{ color: '#6b7280' }}>Loading…</p>
            ) : (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: phaMutation.isLoading ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={phaEnabled}
                  onChange={(e) => phaMutation.mutate(e.target.checked)}
                  disabled={phaMutation.isLoading}
                />
                <span style={{ fontSize: '0.95rem', color: '#374151' }}>
                  Enable player help assistant for logged-in players
                </span>
              </label>
            )}
          </ContentCard>
        )}

        {activeSection === 'player-help-wizard' && (
          <ContentCard>
            <Title>Player help wizard</Title>
            <Subtitle>
              Toggles the decision-tree help wizard in the player sidebar (Phase 2). Separate from the alpha chat
              assistant. Environment variable{' '}
              <code style={{ fontSize: '0.85em', background: '#f3f4f6', padding: '0.1rem 0.35rem', borderRadius: 4 }}>
                HELP_WIZARD_ENABLED
              </code>{' '}
              must not be set to &quot;false&quot;.
            </Subtitle>
            {phwSettingLoading ? (
              <p style={{ color: '#6b7280' }}>Loading…</p>
            ) : (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: phwMutation.isLoading ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={phwEnabled}
                  onChange={(e) => phwMutation.mutate(e.target.checked)}
                  disabled={phwMutation.isLoading}
                />
                <span style={{ fontSize: '0.95rem', color: '#374151' }}>
                  Enable player help wizard for logged-in players
                </span>
              </label>
            )}
          </ContentCard>
        )}

        {activeSection === 'phase2-audit' && (
          <ContentCard>
            <Title>Phase 2 Membership Audit</Title>
            <Subtitle>Verify linked player profiles for all team members and export an Excel report for client communication.</Subtitle>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button type="button" onClick={handleExportPhase2Audit} disabled={auditExportLoading}>
                {auditExportLoading ? 'Preparing…' : 'Download Excel Report'}
              </Button>
            </div>

            {phase2AuditLoading ? (
              <p>Loading Phase 2 audit…</p>
            ) : !phase2Audit ? (
              <p style={{ color: '#6b7280' }}>No audit data available.</p>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Active members</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#111827' }}>{phase2Audit.summary?.activeMembers ?? 0}</div>
                  </div>
                  <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '8px', padding: '0.75rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#065f46' }}>Linked members</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#065f46' }}>{phase2Audit.summary?.linkedMembers ?? 0}</div>
                  </div>
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#991b1b' }}>Unlinked members</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#991b1b' }}>{phase2Audit.summary?.unlinkedMembers ?? 0}</div>
                  </div>
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '0.75rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#1e40af' }}>Linked %</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e40af' }}>{phase2Audit.summary?.linkedPercentage ?? 0}%</div>
                  </div>
                </div>

                <p style={{ marginBottom: '1rem', color: phase2Audit.summary?.readiness === 'ready_for_strict_mode' ? '#065f46' : '#92400e' }}>
                  Status: {phase2Audit.summary?.readiness === 'ready_for_strict_mode' ? 'Ready for strict mode' : 'Needs review before strict mode'}
                </p>

                <Title as="h2" style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>By tenant</Title>
                <TableScrollWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Tenant</Th>
                        <Th>Active</Th>
                        <Th>Linked</Th>
                        <Th>Unlinked</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(phase2Audit.byTenant || []).map((r) => (
                        <Tr key={`${r.tenant_id}-${r.tenant_name}`}>
                          <Td>{r.tenant_name}</Td>
                          <Td>{r.active_members}</Td>
                          <Td>{r.linked_members}</Td>
                          <Td>{r.unlinked_members}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScrollWrapper>

                <Title as="h2" style={{ fontSize: '1.1rem', margin: '1rem 0 0.5rem 0' }}>Unresolved members</Title>
                {(phase2Audit.unresolvedMembers || []).length === 0 ? (
                  <p style={{ color: '#065f46' }}>No unresolved members. All active team members are linked.</p>
                ) : (
                  <TableScrollWrapper>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Team</Th>
                          <Th>Member</Th>
                          <Th>Email</Th>
                          <Th>Tenant</Th>
                          <Th>Matching player exists</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {(phase2Audit.unresolvedMembers || []).map((m) => (
                          <Tr key={m.member_id}>
                            <Td>{m.team_name || m.team_id}</Td>
                            <Td>{`${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}</Td>
                            <Td>{m.email || '—'}</Td>
                            <Td>{m.tenant_name || m.tenant_id}</Td>
                            <Td>{m.has_matching_player ? 'Yes' : 'No'}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableScrollWrapper>
                )}
              </>
            )}
          </ContentCard>
        )}

      </MainContent>
    </PageContainer>
  );
};

export default SuperAdminTenantsPage;
